/**
 * Insights context (read model): the Dashboard's derived numbers, pure.
 *
 * Everything here is arithmetic over what the SQL already returned, kept out of
 * the page so it is unit-tested rather than eyeballed on a chart. No I/O, and
 * no "today" of its own: the anchor date is always passed in, which is what
 * makes the week maths testable at all.
 */

/** Whole-number percent of `part` in `whole`. A zero whole has no share. */
export function share(part: number, whole: number): number {
  if (whole <= 0) return 0;
  return Math.round((part / whole) * 100);
}

/**
 * Percent change from `previous` to `current`, or **null** when there is no
 * previous figure to compare against. Null rather than 0 or 100: "no change"
 * and "nothing to compare" are different statements, and showing "+100%"
 * against an empty month is the kind of number that gets quoted back at you.
 */
export function percentChange(current: number, previous: number): number | null {
  if (previous <= 0) return null;
  return Math.round(((current - previous) / previous) * 100);
}

/** The Monday of the ISO week containing `date` (a "YYYY-MM-DD" string). */
export function mondayOf(date: string): string {
  const d = new Date(`${date}T00:00:00Z`);
  // getUTCDay: 0 = Sunday. Shift so Monday is the start of the week.
  const shift = (d.getUTCDay() + 6) % 7;
  d.setUTCDate(d.getUTCDate() - shift);
  return d.toISOString().slice(0, 10);
}

/** The Mondays of the `count` weeks ending with the week containing `today`. */
export function recentMondays(today: string, count: number): string[] {
  const current = mondayOf(today);
  const out: string[] = [];
  for (let i = count - 1; i >= 0; i -= 1) {
    const d = new Date(`${current}T00:00:00Z`);
    d.setUTCDate(d.getUTCDate() - 7 * i);
    out.push(d.toISOString().slice(0, 10));
  }
  return out;
}

/**
 * Line the week counts up against a fixed set of Mondays, filling the gaps.
 *
 * The query only returns weeks that had bookings, so a quiet week comes back
 * missing rather than zero. Plotted straight, three busy weeks and one silent
 * one would render as three bars and read as "no data for that week" instead
 * of "nobody came".
 */
export function fillWeeks(
  points: { weekStart: string; count: number }[],
  mondays: string[],
): { weekStart: string; count: number }[] {
  const found = new Map(points.map((p) => [p.weekStart, p.count]));
  return mondays.map((weekStart) => ({
    weekStart,
    count: found.get(weekStart) ?? 0,
  }));
}

/** "4 Aug", short enough to sit under a bar. */
export function weekBarLabel(weekStart: string): string {
  return new Date(`${weekStart}T12:00:00Z`).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    timeZone: "UTC",
  });
}

/** The opening hours a demand chart covers: 8am to 8pm, which is every clinic. */
export const CHART_HOURS = [8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19] as const;

/**
 * Counts for each hour of `CHART_HOURS`, with anything outside that range
 * folded into the nearest end rather than dropped. A 7am home visit is real
 * work and should not silently vanish from the chart that claims to show when
 * people want appointments.
 */
export function hoursForChart(
  points: { hour: number; count: number }[],
): { hour: number; count: number }[] {
  const first = CHART_HOURS[0];
  const last = CHART_HOURS[CHART_HOURS.length - 1];
  const totals = new Map<number, number>(CHART_HOURS.map((h) => [h, 0]));
  for (const point of points) {
    const bucket = point.hour < first ? first : point.hour > last ? last : point.hour;
    totals.set(bucket, (totals.get(bucket) ?? 0) + point.count);
  }
  return CHART_HOURS.map((hour) => ({ hour, count: totals.get(hour) ?? 0 }));
}
